/**
  * @class mmodChats
  * @description
  * Chats (Messages) module allows sending messages between units (users).
  * Messages are organised as chats with one or more participants,
  * where each can send and receive message.
  * Any participant can close the conversation,
  * which opens again if the other party sends another message.
  * Module is implemented as workflow wfChats.
  * This is a client module named mmodChats used in both, mPatrol and mOffice.
  */

import mmodWorkflow from 'mmodWorkflow'

export default class mmodChats extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: { type: 'object', default: { type: 'full' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      workflow_server: { type: 'string', default: ':msvrWfChats', description: 'Listener acting as the server-side for the workflow' },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  initWindow() {
    this.subscribe('#NOTIFICATIONS')
    super.initWindow()
  }

  // @TODO: limit number of visible chat msg, on scroll load older

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * this method could be overwritten by derived classes to compute calculated properties
  * it is called just before update after received mmObjects
  */
  computeCalculatedFields(obj) {
    let players = _.toString(obj.players).split('∫')
    players = players.filter(p=>p!=this.user_id)
    obj.label = players.slice(0, 2).map(p=>`{{user.fullname.${p}}}`).join(', ')
    const wf = 'wf.'+this.workflow.id
    if(players.length>2) obj.label += ` + ${players.length-2} {{${wf}.of_others}}`
    obj.label2 = `{{${wf}.started_dt}}: {{datehm:${obj.created_dt}}}`
    const unread = this._calcCount(obj._logs)
    obj.count = unread ? `(${unread})`: ''
  } // computeCalculatedFields

  _calcCount(logs) {
    if(!logs) return
    const lastItem = (items, param = 'log_id') => {
      return _.maxBy(items, item=>parseInt(item[param]))
    }

    const items = Object.values(logs)
    const messages = items.filter(i=>i.log_action == 'MSG')
    const last_message = lastItem(messages)

    if(!last_message) return
    if(parseInt(last_message.log_userid) == parseInt(this.user_id)) return

    const seen = items.filter(i=>i.log_action == 'READ' && i.log_userid == this.user_id)
    if(!seen) return
    const last_seen = lastItem(seen)
    if(!last_seen) return
    const new_messages = messages.filter(i=> parseInt(i.log_id) > parseInt(last_seen.log_id))

    return new_messages.length
  }

  openAction(record, form_name, desired_actions) {
    delete record.log_msg
    const elForm = super.openAction(record, form_name, desired_actions)
    if(!elForm) return
    const elLogs = elForm.getItemElement('logs')
    if(elLogs) elLogs.scrollBottom()
    const elMessage = elForm.getItemElement('log_msg')
    if(elMessage) elMessage.value = ''

    if(this.newmessage_id && this.newmessage_id != '') {
      const elPlayers = elForm.getItemElement('players')
      if(elPlayers) {
        elPlayers.value = this.newmessage_id
      }
      delete this.newmessage_id
    }
  } // openAction

  computeLogFields(items) {
    const filtered = Object.values(items).filter(obj=>{ return /DELIVER|READ/.test(obj.log_action) && obj.log_userid != this.user_id })
    const last = _.maxBy(filtered, i=>parseInt(i.log_id))
    return items.map(item => {
      if(/DELIVER|READ/.test(item.log_action)) {
        item.label = this.tr(item.log_action)
        item.class = 'hidden'
        // add class of last to last status msg ]
        if(item && last && parseInt(item.log_id) == parseInt(last.log_id)) {
          item.class = 'status'
        }
      } else {
        item.class = item.log_userid == this.user_id ? 'outgoing' : 'incoming'
        _.set(item, 'data.icon', 'user.icon.'+item.log_userid)
      }
    })
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to mmodChats to open conversation with given user
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.user_id] - user ID of recipient
  */
  mmChatNew(sender, params) {
    // find if chat with this user exists
    const existing_chat = this.elList.items.find(i=> {
      const p = i.data.players.split('∫')
      return p.length == 2 && p.includes(String(params.user_id))
    })
    if(existing_chat && existing_chat.key) {
      this.elList.selected = [existing_chat.key]
    } else {
      // start new chat
      this.newmessage_id = params.user_id
      this.publish('#ME', 'mmFormNew', { holder: 'list' })
    }
  } // mmChatNew

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Creates notification
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.id - reference id that will be sent back to requesting listener when a button is clicked
  * @param {string} [params.message] - Text of the notification
  * @param {string} [params.icon] - icon of the notification
  * @param {string} [params.color] - color label on the notification
  * @param {number} [params.priority] - sets the order in which notifications are displayed. The higher the number, the higher the order in notification list
  * @param {number} [params.timeout] - sets the timeout after which the notification will disappear from target device
  * @param {string} [params.position] - sets the position of action buttons, options: left, center, right
  * @param {boolean} [params.selected] - Shows if the notification element is preopened
  * @param {string} [params.sound] - alert sound
  * @param {array} [params.actions] - Actions available to respond to notification
  */
  mmNotify(sender, params) {
    if(sender && sender.listener_id == 'mmodChats') {
      const chatVisible = this.el.focused
      const elSelected = this.elList.selected[0] == params.id
      const tabVisible = document.visibilityState == 'visible'
      const action = (tabVisible && chatVisible && elSelected) ? 'READ' : 'DELIVER'
      let item = this.elList.getItemElement(params.id)
      if(!item) return
      item = item.data
      const toggleVisibility = ()=>{
        if(document.visibilityState == 'visible') {
          document.removeEventListener('visibilitychange', toggleVisibility)
          if(this.canChangeState(item)) {
            this._updateObject(params.id, 'READ')
          }
        }
      }

      if(this.canChangeState(item) && item.state != 'SEEN') {
        this._updateObject(params.id, action)
        if(chatVisible && elSelected && !tabVisible) {
          document.addEventListener('visibilitychange', toggleVisibility)
        }
      }
    }
  } // mmNotify

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by <mele-list> when selected item change
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of the list
  * @param {string} params.selected - key attribute of the selected items separated by ∫
  * @param {string} [params.old] - key attribute of previously selected item
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmListSelected(sender, params) {
    super.mmListSelected(sender, params)
    let item = this.elList.getItemElement(params.selected)
    if(!item) return
    item = item.data
    if(this.canChangeState(item) && item.state != 'SEEN') {
      this._updateObject(item.id, 'READ')
    }
  } // mmListSelected

  _updateObject(id, action) {
    this.serverRequest('UpdatingObject', 'mmAction', { id, action })
  }

  canChangeState(item) {
    const logs = _.filter(item._logs, i=>!/DELIVER|READ/.test(i.log_action)) // filterout status objects
    const last = logs[Math.max(...Object.keys(logs))] // state can be changed if I was not creator of msg
    return last.log_userid != this.user_id
  }

  _getLastRelevantLog(item) {

  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server notifies watchers that some system users were updated
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - whether all fields or just subset is sent
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} params.version - version of data sent by server
  * @param {array} params.list - array of changed records
  */
  mmUsers(sender, params) {
    super.mmUsers(sender, params)
    this.el.forAll('.mele-list[key=list]', el=>el.autoChanged(true))
  } // mmUsers

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    // closed chats are treated as wiped items - completely removed
    params.list.forEach(item=>{
      if(item.state=='CLOSED') item.state = 'WIPED'
    })
    super.mmObjects(sender, params)
  } // mmObjects
} // mmodChats
